import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getUserProfile, createJobPost, getCompanyById } from '../services/api';

const JobPostingPage = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [formData, setFormData] = useState({
    position: '',
    vacancies: '',
    location: '',
    salary: '',
    type: 'Full-time',
    requiredSkills: '',
    description: '',
    responsibilities: '',
    requirements: '',
    benefits: '',
    companyLink: ''
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const loadProfile = async () => {
      const result = await getUserProfile(currentUser.uid);
      if (result.success && result.profile) {
        setProfile(result.profile);

        // Get company name
        if (result.profile.customCompanyName) {
          // If employer used custom company name
          setCompanyName(result.profile.customCompanyName);
        } else if (result.profile.companyId) {
          // If employer selected from company list
          const companyResult = await getCompanyById(result.profile.companyId);
          if (companyResult.success && companyResult.company) {
            setCompanyName(companyResult.company.name);
          }
        } else {
          // No company information
          setMessage('Please complete your profile with company information before posting a job.');
        }
      } else {
        setMessage('Please complete your profile first.');
      }
    };

    loadProfile();
  }, [currentUser]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    // Validate company info
    if (!profile?.companyId && !profile?.customCompanyName) {
      setMessage('Please complete your profile with company information first.');
      setLoading(false);
      return;
    }

    // Convert comma-separated strings to arrays
    const skillsArray = formData.requiredSkills
      .split(',')
      .map(skill => skill.trim())
      .filter(skill => skill.length > 0);

    const responsibilitiesArray = formData.responsibilities
      .split('\n')
      .map(item => item.trim())
      .filter(item => item.length > 0);

    const requirementsArray = formData.requirements
      .split('\n')
      .map(item => item.trim())
      .filter(item => item.length > 0);

    const benefitsArray = formData.benefits
      .split('\n')
      .map(item => item.trim())
      .filter(item => item.length > 0);

    const jobPostData = {
      ...formData,
      vacancies: parseInt(formData.vacancies),
      requiredSkills: skillsArray,
      responsibilities: responsibilitiesArray,
      requirements: requirementsArray,
      benefits: benefitsArray,
      companyName: companyName, // Auto-filled from profile
      companyId: profile.companyId || null,
      customCompanyName: profile.customCompanyName || null,
      employerId: currentUser.uid,
      employerName: profile.name || currentUser.displayName,
      status: 'pending', // Pending admin approval
      postedDate: new Date().toLocaleDateString()
    };

    const result = await createJobPost(jobPostData);

    if (result.success) {
      setMessage('Job posted successfully! Waiting for admin approval.');
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } else {
      setMessage('Error posting job. Please try again.');
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="bg-white rounded-xl shadow-2xl p-8 border-t-4 border-blue-500">
          <div className="flex items-center mb-6">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4 rounded-xl mr-4">
              <span className="text-3xl">📝</span>
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-800">
                Post a New Job
              </h1>
              <p className="text-gray-500">Fill in the details to post a job opportunity</p>
            </div>
          </div>

          {message && (
            <div className={`mb-6 p-4 rounded-lg shadow-md ${
              message.includes('success')
                ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500 text-green-700'
                : 'bg-gradient-to-r from-red-50 to-rose-50 border-l-4 border-red-500 text-red-700'
            }`}>
              <div className="flex items-center">
                <span className="text-2xl mr-3">{message.includes('success') ? '✅' : '❌'}</span>
                <span className="font-medium">{message}</span>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border-l-4 border-blue-500">
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                <span className="text-xl mr-2">💼</span>
                Basic Information
              </h3>

              <div className="space-y-4">
                {/* Company Name - Auto-filled and Read-only */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Company Name
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={companyName}
                      disabled
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-gray-100 text-gray-700 cursor-not-allowed"
                    />
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <span className="text-green-600 text-xl">🔒</span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    💡 Auto-filled from your profile. Cannot be changed.
                  </p>
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Company Application Link (Optional)
                  </label>
                  <input
                    type="url"
                    name="companyLink"
                    value={formData.companyLink}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    placeholder="e.g., https://yourcompany.com/careers"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    💡 If not provided, "Apply Now" will redirect to binarytechstation.com
                  </p>
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Job Position/Title *
                  </label>
                  <input
                    type="text"
                    name="position"
                    value={formData.position}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    placeholder="e.g., Senior React Developer"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-gray-700 font-semibold mb-2">
                      Number of Vacancies *
                    </label>
                    <input
                      type="number"
                      name="vacancies"
                      value={formData.vacancies}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      placeholder="e.g., 2"
                      min="1"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-gray-700 font-semibold mb-2">
                      Location *
                    </label>
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      placeholder="e.g., Remote"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-gray-700 font-semibold mb-2">
                      Job Type *
                    </label>
                    <select
                      name="type"
                      value={formData.type}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      required
                    >
                      <option value="Full-time">Full-time</option>
                      <option value="Part-time">Part-time</option>
                      <option value="Contract">Contract</option>
                      <option value="Internship">Internship</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Salary Range *
                  </label>
                  <input
                    type="text"
                    name="salary"
                    value={formData.salary}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    placeholder="e.g., $80k - $120k"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Required Skills (comma-separated) *
                  </label>
                  <input
                    type="text"
                    name="requiredSkills"
                    value={formData.requiredSkills}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    placeholder="e.g., React, JavaScript, Node.js, TypeScript"
                    required
                  />
                  <p className="text-sm text-gray-500 mt-1">💡 Separate skills with commas</p>
                </div>
              </div>
            </div>

            {/* Job Description */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border-l-4 border-green-500">
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                <span className="text-xl mr-2">📋</span>
                Job Description
              </h3>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  Description *
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                  placeholder="Provide a brief overview of the role and what you're looking for..."
                  required
                ></textarea>
              </div>
            </div>

            {/* Responsibilities */}
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border-l-4 border-purple-500">
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                <span className="text-xl mr-2">✓</span>
                Responsibilities
              </h3>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  Key Responsibilities (one per line) *
                </label>
                <textarea
                  name="responsibilities"
                  value={formData.responsibilities}
                  onChange={handleChange}
                  rows="5"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                  placeholder="Develop and maintain web applications&#10;Collaborate with cross-functional teams&#10;Write clean and maintainable code"
                  required
                ></textarea>
                <p className="text-sm text-gray-500 mt-1">💡 Enter each responsibility on a new line</p>
              </div>
            </div>

            {/* Requirements */}
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-6 border-l-4 border-yellow-500">
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                <span className="text-xl mr-2">🎯</span>
                Requirements
              </h3>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  Job Requirements (one per line) *
                </label>
                <textarea
                  name="requirements"
                  value={formData.requirements}
                  onChange={handleChange}
                  rows="5"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all"
                  placeholder="5+ years of experience with React&#10;Strong understanding of JavaScript&#10;Bachelor's degree in Computer Science"
                  required
                ></textarea>
                <p className="text-sm text-gray-500 mt-1">💡 Enter each requirement on a new line</p>
              </div>
            </div>

            {/* Benefits */}
            <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-6 border-l-4 border-indigo-500">
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                <span className="text-xl mr-2">🎁</span>
                Benefits & Perks
              </h3>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  Benefits (one per line) *
                </label>
                <textarea
                  name="benefits"
                  value={formData.benefits}
                  onChange={handleChange}
                  rows="5"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
                  placeholder="Competitive salary and equity&#10;Health, dental, and vision insurance&#10;Flexible work hours&#10;Professional development budget"
                  required
                ></textarea>
                <p className="text-sm text-gray-500 mt-1">💡 Enter each benefit on a new line</p>
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-4 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-4 rounded-xl font-bold hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <span className="animate-spin mr-2">⏳</span>
                    Posting...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    📝 Post Job
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="flex-1 bg-gradient-to-r from-gray-400 to-gray-500 text-white py-4 rounded-xl font-bold hover:from-gray-500 hover:to-gray-600 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                ← Back to Dashboard
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default JobPostingPage;
